import React, { useEffect, useState } from "react";
import api, { formatIDR } from "../lib/api";
import { toast } from "sonner";
import { X, ExternalLink, Ban, AlertTriangle, Copy } from "lucide-react";

// Badge tanda duplikat untuk reimbursement geo-absen (dipakai di list Payroll & galeri)
export const ReimburseFlags = ({ flags, compact = false }) => {
  if (!flags || !flags.length) return null;
  return (
    <div className={`flex flex-wrap gap-1 ${compact ? "mt-0.5" : "mt-1"}`}>
      {flags.map((f, i) => {
        const dup = f.type === "double_upload" || f.type === "double_struk";
        const cls = dup
          ? "bg-[#FEE2E2] text-[#B91C1C] border-[#FCA5A5]"
          : f.type === "suspect"
            ? "bg-[#FEF3C7] text-[#B45309] border-[#FCD34D]"
            : "bg-[#FFEDD5] text-[#C2410C] border-[#FDBA74]";
        return (
          <span key={i} title={f.label} className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] font-bold leading-tight ${cls}`}>
            {dup ? <Copy size={10} /> : <AlertTriangle size={10} />}
            <span className={compact ? "truncate max-w-[220px]" : ""}>{f.label}</span>
          </span>
        );
      })}
    </div>
  );
};

export const reimburseRowTone = (flags) => {
  if (!flags || !flags.length) return "";
  if (flags.some((f) => f.type === "double_upload" || f.type === "double_struk")) return "bg-[#FEF2F2] border-l-2 border-l-[#DC2626]";
  return "bg-[#FFF7ED] border-l-2 border-l-[#F97316]";
};

const ReimburseGallery = ({ open, onClose, items, checkedIds, techName, onRejected }) => {
  const [busy, setBusy] = useState("");
  const [zoom, setZoom] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") (zoom ? setZoom(null) : onClose()); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, zoom, onClose]);

  if (!open) return null;

  const flaggedCount = items.filter((r) => (r.flags || []).length).length;
  const shown = filter === "flagged" ? items.filter((r) => (r.flags || []).length) : items;
  const total = items.reduce((a, r) => a + (Number(r.amount) || 0), 0);

  const reject = async (r) => {
    const reason = window.prompt(
      `Reject & hapus bon ${r.date} senilai ${formatIDR(r.amount)}?\n\nBon dikeluarkan dari reimbursement (karyawan melihatnya sebagai "Ditolak admin") dan dari payroll tersimpan.\n\nAlasan (opsional):`,
      (r.flags || []).some((f) => f.type === "double_upload" || f.type === "double_struk") ? "Struk/bon ganda" : "",
    );
    if (reason === null) return;
    setBusy(r.id);
    try {
      const { data } = await api.post(`/payroll/reimbursements/${r.id}/reject`, { reason });
      toast.success(`Bon ${r.date} ${formatIDR(r.amount)} ditolak & dihapus` + (data.payrolls_updated ? " — payroll tersimpan ikut diperbarui" : ""));
      onRejected(r.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal reject bon");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-stretch justify-center p-2 sm:p-4" data-testid="reimburse-gallery" onClick={onClose}>
      <div className="bg-white w-full max-w-7xl flex flex-col min-h-0" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-[#E4E4E7]">
          <div className="min-w-0">
            <div className="font-bold text-sm truncate">Galeri Bon / Struk — {techName}</div>
            <div className="text-[11px] text-[#52525B]">
              {items.length} bon · total {formatIDR(total)}
              {flaggedCount > 0 && <span className="text-[#B91C1C] font-bold"> · {flaggedCount} ditandai</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {[["all", "Semua"], ["flagged", `Ditandai (${flaggedCount})`]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setFilter(v)} data-testid={`reimburse-gallery-filter-${v}`}
                className={`text-[11px] font-bold uppercase px-2 py-1 border ${filter === v ? "bg-[#09090B] text-white border-[#09090B]" : "border-[#E4E4E7] text-[#52525B]"}`}>{l}</button>
            ))}
            <button type="button" onClick={onClose} className="p-1.5 hover:bg-[#F4F4F5]" data-testid="reimburse-gallery-close" aria-label="Tutup"><X size={18} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 bg-[#FAFAFA]">
          {shown.length === 0 ? (
            <div className="text-center text-sm text-[#52525B] py-16">Tidak ada bon {filter === "flagged" ? "yang ditandai" : ""}.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {shown.map((r) => {
                const dup = (r.flags || []).some((f) => f.type === "double_upload" || f.type === "double_struk");
                const checked = checkedIds.includes(r.id);
                return (
                  <div key={r.id} data-testid={`reimburse-gallery-item-${r.id}`}
                    className={`bg-white border flex flex-col ${dup ? "border-[#DC2626] ring-1 ring-[#DC2626]" : (r.flags || []).length ? "border-[#F97316]" : "border-[#E4E4E7]"}`}>
                    <button type="button" className="relative aspect-[3/4] bg-[#F4F4F5] overflow-hidden" onClick={() => r.thumb_url && setZoom(r)}>
                      {r.thumb_url ? (
                        <img src={r.thumb_url} alt={`Bon ${r.date}`} loading="lazy" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[11px] text-[#A1A1AA]">Tanpa foto</div>
                      )}
                      {!checked && <span className="absolute top-1 left-1 bg-black/70 text-white text-[9px] font-bold uppercase px-1.5 py-0.5">Tidak ditagihkan</span>}
                    </button>
                    <div className="p-2 flex-1 flex flex-col gap-1">
                      <div className="flex items-center justify-between gap-1">
                        <span className="font-mono text-[11px]">{r.date}</span>
                        <span className="font-mono font-bold text-sm">{formatIDR(r.amount)}</span>
                      </div>
                      {r.ocr && (
                        <div className="text-[10px] text-[#52525B] leading-snug">
                          {[r.ocr.merchant, r.ocr.time, r.ocr.receipt_no ? `No. ${r.ocr.receipt_no}` : ""].filter(Boolean).join(" · ")}
                          {r.ocr.amount > 0 && Number(r.ocr.amount) !== Number(r.amount) && (
                            <span className="block text-[#B91C1C] font-bold">Struk terbaca {formatIDR(r.ocr.amount)}</span>
                          )}
                        </div>
                      )}
                      {r.remark && <div className="text-[10px] text-[#A1A1AA] truncate" title={r.remark}>{r.remark}</div>}
                      <ReimburseFlags flags={r.flags} />
                      <div className="mt-auto pt-1 flex items-center justify-between gap-2">
                        {r.photo_url ? (
                          <a href={r.photo_url} target="_blank" rel="noreferrer" className="text-[10px] font-bold text-[#002FA7] inline-flex items-center gap-0.5 hover:underline">Buka <ExternalLink size={10} /></a>
                        ) : <span />}
                        <button type="button" disabled={busy === r.id} onClick={() => reject(r)} data-testid={`reimburse-reject-${r.id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 bg-[#DC2626] text-white hover:bg-[#B91C1C] disabled:opacity-50">
                          <Ban size={11} /> {busy === r.id ? "..." : "Reject"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {zoom && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex flex-col items-center justify-center p-4" onClick={(e) => { e.stopPropagation(); setZoom(null); }}>
          <img src={zoom.thumb_url} alt="" className="max-h-[85vh] max-w-full object-contain" />
          <div className="text-white text-sm mt-2 font-mono">{zoom.date} · {formatIDR(zoom.amount)}</div>
        </div>
      )}
    </div>
  );
};

export default ReimburseGallery;
